import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, GraduationCap, Hand, Plus, Users, X } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { useApi, useFetch, errMsg } from "@/lib/api";
import { fmtLong, isPast, nextSchoolDay, today } from "@/lib/dates";
import { Card, ConfirmDelete, Empty, ErrorBox, FormSheet, Loading, PageTitle, SubjectBadge } from "@/components/common";
import { Field, OptionSelect, SubjectSelect } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUS = {
  in_attesa: { label: "In attesa", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  confermato: { label: "Confermato", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  rifiutato: { label: "Non accettato", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
};

function AddAssessmentSheet({ open, onOpenChange, defaultKind, onSaved }) {
  const api = useApi();
  const empty = { kind: defaultKind, subject: "", date: nextSchoolDay(today()), description: "", max_candidates: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ ...empty, kind: defaultKind }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const save = async (e) => {
    e.preventDefault();
    if (!form.subject || !form.date) return toast.error("Compila materia e data");
    setBusy(true);
    try {
      await api("post", "/assessments", { ...form, max_candidates: form.max_candidates ? Number(form.max_candidates) : null });
      toast.success("Prova programmata");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet open={open} onOpenChange={onOpenChange} title="Programma una prova">
      <form onSubmit={save} className="space-y-4" data-testid="assessment-form">
        <Field label="Tipo"><OptionSelect value={form.kind} onChange={set("kind")} testId="assessment-kind" options={[{ value: "interrogazione", label: "Interrogazione" }, { value: "verifica", label: "Verifica" }]} /></Field>
        <Field label="Materia"><SubjectSelect value={form.subject} onChange={set("subject")} testId="assessment-subject" /></Field>
        <Field label="Data"><Input type="date" className="h-11" value={form.date} onChange={(e) => set("date")(e.target.value)} data-testid="assessment-date" /></Field>
        <Field label="Posti per le candidature" hint="Lascia vuoto per nessun limite">
          <Input type="number" inputMode="numeric" min="1" className="h-11" value={form.max_candidates} onChange={(e) => set("max_candidates")(e.target.value)} data-testid="assessment-max" />
        </Field>
        <Field label="Note (facoltativo)"><Textarea rows={3} value={form.description} onChange={(e) => set("description")(e.target.value)} placeholder="Es. Capitoli 3 e 4" data-testid="assessment-description" /></Field>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-full text-base" data-testid="assessment-submit">Salva</Button>
      </form>
    </FormSheet>
  );
}

function AssessmentCard({ a, me, isAdmin, onChange, onDelete }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const mine = a.candidates.find((c) => c.member_id === me.id);
  const active = a.candidates.filter((c) => c.status !== "rifiutato").length;
  const full = a.max_candidates && active >= a.max_candidates;
  const past = isPast(a.date);
  const call = async (method, path, body, msg) => {
    setBusy(true);
    try { onChange(await api(method, path, body)); if (msg) toast.success(msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card className={`fade-up ${past ? "opacity-70" : ""}`} data-testid={`assessment-card-${a.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <SubjectBadge subject={a.subject} />
          <p className="mt-1.5 font-heading text-lg font-bold">{fmtLong(a.date)}</p>
          {a.description && <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{a.description}</p>}
        </div>
        {isAdmin && <ConfirmDelete testId={`assessment-delete-${a.id}`} onConfirm={onDelete} />}
      </div>
      <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Users className="h-4 w-4" />Candidati: {active}{a.max_candidates ? ` / ${a.max_candidates}` : ""}
      </div>
      {a.candidates.length > 0 && (
        <ul className="mt-2 space-y-1.5" data-testid={`assessment-candidates-${a.id}`}>
          {a.candidates.map((c) => (
            <li key={c.member_id} className="flex items-center justify-between gap-2 rounded-xl bg-secondary/60 px-3 py-2 text-sm">
              <span className="min-w-0 truncate font-medium">{c.name}</span>
              <span className="flex shrink-0 items-center gap-1">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS[c.status].cls}`}>{STATUS[c.status].label}</span>
                {isAdmin && c.status !== "confermato" && <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busy} onClick={() => call("patch", `/assessments/${a.id}/candidates/${c.member_id}`, { status: "confermato" })} data-testid={`candidate-confirm-${a.id}-${c.member_id}`} aria-label="Conferma"><Check className="h-4 w-4 text-emerald-600" /></Button>}
                {isAdmin && c.status !== "rifiutato" && <Button size="icon" variant="ghost" className="h-8 w-8" disabled={busy} onClick={() => call("patch", `/assessments/${a.id}/candidates/${c.member_id}`, { status: "rifiutato" })} data-testid={`candidate-reject-${a.id}-${c.member_id}`} aria-label="Rifiuta"><X className="h-4 w-4 text-rose-600" /></Button>}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!past && (
        <Button className="mt-3 h-11 w-full rounded-full" variant={mine ? "outline" : "default"} disabled={busy || (!mine && full)}
          onClick={() => call("post", `/assessments/${a.id}/candidatura`, null, mine ? "Candidatura ritirata" : "Candidatura inviata")}
          data-testid={`btn-candidati-${a.id}`}>
          <Hand className="mr-1.5 h-4 w-4" />{mine ? "Ritira candidatura" : full ? "Posti esauriti" : "Mi propongo"}
        </Button>
      )}
    </Card>
  );
}

export default function Verifiche() {
  const api = useApi();
  const { me, isAdmin } = useMe();
  const { data, error, reload, setData } = useFetch("/assessments");
  const [kind, setKind] = useState("interrogazione");
  const [showPast, setShowPast] = useState(false);
  const [adding, setAdding] = useState(false);

  if (error) return <ErrorBox text={error} onRetry={reload} />;
  if (!data) return <Loading />;
  const list = data.filter((a) => a.kind === kind);
  const upcoming = list.filter((a) => !isPast(a.date));
  const past = list.filter((a) => isPast(a.date)).reverse();
  const replace = (item) => setData((all) => all.map((x) => (x.id === item.id ? item : x)));
  const remove = async (id) => {
    try { await api("delete", `/assessments/${id}`); toast.success("Eliminata"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const render = (a) => <AssessmentCard key={a.id} a={a} me={me} isAdmin={isAdmin} onChange={replace} onDelete={() => remove(a.id)} />;

  return (
    <div>
      <PageTitle title="Verifiche e interrogazioni" subtitle="Proponiti per essere interrogato"
        action={isAdmin && <Button className="rounded-full" onClick={() => setAdding(true)} data-testid="btn-add-assessment"><Plus className="mr-1 h-4 w-4" />Nuova</Button>} />
      <Tabs value={kind} onValueChange={setKind} className="mb-5">
        <TabsList className="grid h-11 w-full grid-cols-2 rounded-full sm:max-w-sm">
          <TabsTrigger value="interrogazione" className="rounded-full" data-testid="verifiche-tab-interrogazioni">Interrogazioni</TabsTrigger>
          <TabsTrigger value="verifica" className="rounded-full" data-testid="verifiche-tab-verifiche">Verifiche</TabsTrigger>
        </TabsList>
      </Tabs>
      {upcoming.length === 0 ? <Empty icon={GraduationCap} text="Nulla in programma." testId="verifiche-empty" /> : (
        <div className="grid gap-3 md:grid-cols-2">{upcoming.map(render)}</div>
      )}
      {past.length > 0 && (
        <div className="mt-6">
          <Button variant="ghost" className="rounded-full" onClick={() => setShowPast(!showPast)} data-testid="verifiche-toggle-past">
            {showPast ? "Nascondi passate" : `Mostra passate (${past.length})`}
          </Button>
          {showPast && <div className="mt-3 grid gap-3 md:grid-cols-2">{past.map(render)}</div>}
        </div>
      )}
      <AddAssessmentSheet open={adding} onOpenChange={setAdding} defaultKind={kind} onSaved={reload} />
    </div>
  );
}
